/**
 * Mobile Medication Logging Page
 * Allows recording medication administration offline with auto-sync
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Save, Pill, Clock, Check, X, AlertTriangle, RefreshCw } from 'lucide-react';
import { queueOfflineAction, ACTION_TYPES } from '@/lib/offline/actionQueue';
import { syncAllActions, isOnline } from '@/lib/offline/syncEngine';
import { SyncStatusBadge, OfflineIndicator, SYNC_STATUS } from '@/components/mobile/SyncStatusBadge';

const MED_STATUSES = [
  { value: 'ADMINISTERED', label: 'Administered', icon: Check, color: 'bg-green-500/10 text-green-600 border-green-500/30' },
  { value: 'REFUSED', label: 'Refused', icon: X, color: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
  { value: 'MISSED', label: 'Missed', icon: AlertTriangle, color: 'bg-red-500/10 text-red-600 border-red-500/30' },
];

export default function MobileMedicationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const participantId = searchParams.get('participantId');
  const medicationId = searchParams.get('medicationId');
  const scheduleId = searchParams.get('scheduleId');

  const [online, setOnline] = useState(isOnline());
  const [saving, setSaving] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [medications, setMedications] = useState([]);
  const [dueMedications, setDueMedications] = useState([]);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [form, setForm] = useState({
    participantId: participantId || '',
    medicationId: medicationId || '',
    scheduleId: scheduleId || '',
    dose: '',
    timeAdministered: new Date().toISOString().slice(0, 16), // YYYY-MM-DDTHH:MM
    status: 'ADMINISTERED',
    notes: '',
  });

  // Online/offline listener
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch participants
  useEffect(() => {
    if (online) {
      api.get('/participants', { params: { pageSize: 100 } })
        .then(res => setParticipants(res.data.items || []))
        .catch(() => {});
    }
  }, [online]);

  // Fetch medications when participant changes
  useEffect(() => {
    if (online && form.participantId) {
      // Fetch participant's medications using correct endpoint
      api.get(`/medications/by-participant/${form.participantId}`, { params: { pageSize: 100 } })
        .then(res => {
          setMedications(res.data.items || res.data.data || []);
        })
        .catch(() => {});
      
      // Fetch due medications
      api.get('/mobile/medications/due', { params: { participantId: form.participantId } })
        .then(res => {
          setDueMedications(res.data.data || []);
        })
        .catch(() => {});
    }
  }, [online, form.participantId]);

  // Update selected medication details
  useEffect(() => {
    if (form.medicationId) {
      const med = medications.find(m => m.id === form.medicationId);
      if (med) {
        setSelectedMedication(med);
        if (!form.dose) {
          setForm(prev => ({ ...prev, dose: med.dosage || '' }));
        }
      }
    }
  }, [form.medicationId, medications]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const selectDueMedication = (dueMed) => {
    setForm(prev => ({
      ...prev,
      medicationId: dueMed.medicationId,
      scheduleId: dueMed.scheduleId,
      participantId: dueMed.participantId,
      dose: dueMed.dose,
    }));
  };

  const saveDraft = async () => {
    if (!form.medicationId) {
      toast.error('Please select a medication');
      return;
    }
    if (!form.participantId) {
      toast.error('Please select a participant');
      return;
    }
    if (!form.status) {
      toast.error('Please select a status');
      return;
    }

    setSaving(true);
    setSyncStatus(SYNC_STATUS.PENDING);

    try {
      // Queue the action for offline sync
      await queueOfflineAction(ACTION_TYPES.MEDICATION_LOG, {
        medicationId: form.medicationId,
        participantId: form.participantId,
        scheduleId: form.scheduleId || null,
        dose: form.dose,
        timeAdministered: form.timeAdministered,
        status: form.status,
        notes: form.notes,
        timestamp: new Date().toISOString(),
      });

      toast.success('Medication record saved locally', {
        description: online ? 'Syncing...' : 'Will sync when online',
      });

      // If online, try to sync immediately
      if (online) {
        setSyncStatus(SYNC_STATUS.SYNCING);
        try {
          const results = await syncAllActions();
          if (results.synced > 0) {
            setSyncStatus(SYNC_STATUS.SYNCED);
            toast.success('Medication record synced');
            setTimeout(() => navigate(-1), 1000);
          } else if (results.failed > 0) {
            setSyncStatus(SYNC_STATUS.FAILED);
          } else if (results.conflicts > 0) {
            setSyncStatus(SYNC_STATUS.FAILED);
            toast.error('Medication already recorded', {
              description: 'This medication was already logged for this time',
            });
          }
        } catch (err) {
          setSyncStatus(SYNC_STATUS.FAILED);
        }
      } else {
        setTimeout(() => navigate(-1), 1500);
      }
    } catch (err) {
      toast.error('Failed to save medication record');
      setSyncStatus(SYNC_STATUS.FAILED);
    } finally {
      setSaving(false);
    }
  };

  const handleRetrySync = async () => {
    setSyncStatus(SYNC_STATUS.SYNCING);
    try {
      const results = await syncAllActions();
      if (results.synced > 0) {
        setSyncStatus(SYNC_STATUS.SYNCED);
        toast.success('Synced successfully');
      } else {
        setSyncStatus(SYNC_STATUS.FAILED);
      }
    } catch (err) {
      setSyncStatus(SYNC_STATUS.FAILED);
      toast.error('Sync failed');
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="mobile-medication-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-primary text-primary-foreground px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary/80" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Pill className="h-5 w-5" />
              <h1 className="text-lg font-bold">Record Medication</h1>
            </div>
            <p className="text-xs opacity-80">Log medication administration</p>
          </div>
        </div>
      </div>

      {/* Offline/Sync Status */}
      <div className="px-4 pt-4 space-y-2">
        <OfflineIndicator isOffline={!online} />
        {syncStatus && (
          <SyncStatusBadge 
            status={syncStatus} 
            onRetry={syncStatus === SYNC_STATUS.FAILED ? handleRetrySync : undefined}
          />
        )}
      </div>

      {/* Due Medications Quick Select */}
      {dueMedications.length > 0 && (
        <div className="px-4 pt-4">
          <Card className="border-amber-500/20 bg-amber-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-600" />
                Due Medications
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="space-y-2">
                {dueMedications.map(med => (
                  <button
                    key={med.scheduleId}
                    onClick={() => selectDueMedication(med)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                      form.medicationId === med.medicationId
                        ? 'border-primary bg-primary/5'
                        : 'border-transparent bg-background hover:bg-muted'
                    }`}
                    data-testid={`due-med-${med.medicationId}`}
                  >
                    <div className="text-left">
                      <p className="font-medium text-sm">{med.medicationName}</p>
                      <p className="text-xs text-muted-foreground">{med.dose} at {med.scheduledTime}</p>
                    </div>
                    {med.isOverdue && (
                      <Badge variant="destructive" className="text-xs">
                        {med.minutesOverdue}min overdue
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Form */}
      <div className="p-4 space-y-4">
        {/* Participant & Medication Selection */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="participant">Participant *</Label>
              <Select 
                value={form.participantId || undefined} 
                onValueChange={(v) => handleChange('participantId', v)}
              >
                <SelectTrigger id="participant" data-testid="participant-select">
                  <SelectValue placeholder="Select participant" />
                </SelectTrigger>
                <SelectContent>
                  {participants.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.first_name} {p.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="medication">Medication *</Label>
              <Select 
                value={form.medicationId || undefined} 
                onValueChange={(v) => handleChange('medicationId', v)}
                disabled={!form.participantId}
              >
                <SelectTrigger id="medication" data-testid="medication-select">
                  <SelectValue placeholder={form.participantId ? "Select medication" : "Select participant first"} />
                </SelectTrigger>
                <SelectContent>
                  {medications.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name} ({m.dosage || 'No dosage'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedMedication && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">{selectedMedication.name}</p>
                {selectedMedication.instructions && (
                  <p className="text-xs text-muted-foreground mt-1">{selectedMedication.instructions}</p>
                )}
                {selectedMedication.requires_double_sign && (
                  <Badge variant="outline" className="mt-2 text-xs">Requires Witness</Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Selection */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Administration Status *</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="grid grid-cols-3 gap-2">
              {MED_STATUSES.map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleChange('status', value)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    form.status === value 
                      ? `${color} border-current` 
                      : 'bg-muted/50 border-transparent hover:bg-muted'
                  }`}
                  data-testid={`status-${value.toLowerCase()}`}
                >
                  <Icon className="h-5 w-5" />
                  {label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Dose & Time */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="dose">Dose</Label>
              <Input
                id="dose"
                placeholder="e.g., 10mg, 2 tablets"
                value={form.dose}
                onChange={(e) => handleChange('dose', e.target.value)}
                data-testid="dose-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="timeAdministered">Time Administered</Label>
              <Input
                id="timeAdministered"
                type="datetime-local"
                value={form.timeAdministered}
                onChange={(e) => handleChange('timeAdministered', e.target.value)}
                data-testid="time-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Notes (Optional)</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Textarea
              id="notes"
              placeholder="Any observations or notes about this administration..."
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              className="resize-none"
              data-testid="notes-input"
            />
          </CardContent>
        </Card>
      </div>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4 safe-area-inset-bottom">
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button 
            className="flex-1"
            onClick={saveDraft}
            disabled={saving || !form.medicationId || !form.participantId || !form.status}
            data-testid="save-medication-btn"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {online ? 'Save & Sync' : 'Save Draft'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
